import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import {
  Package,
  RotateCcw,
  Trash2,
  Clock,
  User,
  Briefcase,
  Search,
  AlertCircle,
  CheckCircle,
  XCircle,
} from 'lucide-react';

interface Checkout {
  id: string;
  tool_unit_id?: string;
  definition_id: string;
  user_id: string;
  job_id?: string;
  machine?: string;
  quantity: number;
  checked_out_at: string;
  expected_return_at?: string;
  returned_at?: string;
  discarded: number;
  user_name: string;
  job_number?: string;
  tool_name: string;
  part_number?: string;
  label_code?: string;
}

interface Location {
  id: string;
  name: string;
  toolroom_name: string;
}

export function Checkout() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'active' | 'returned' | 'all'>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [returningCheckout, setReturningCheckout] = useState<Checkout | null>(null);
  const [discardingCheckout, setDiscardingCheckout] = useState<Checkout | null>(null);

  const { data: checkouts, isLoading } = useQuery({
    queryKey: ['checkouts', statusFilter],
    queryFn: async () => {
      const res = await api.get<{
        items: Checkout[];
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
      }>(`/checkouts?status=${statusFilter}&pageSize=100`);
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  const filteredCheckouts = checkouts?.items?.filter((checkout) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      checkout.tool_name?.toLowerCase().includes(query) ||
      checkout.user_name?.toLowerCase().includes(query) ||
      checkout.job_number?.toLowerCase().includes(query) ||
      checkout.label_code?.toLowerCase().includes(query) ||
      checkout.part_number?.toLowerCase().includes(query)
    );
  });

  const getStatusBadge = (checkout: Checkout) => {
    if (checkout.discarded) {
      return (
        <Badge variant="error">
          <XCircle className="h-3 w-3 mr-1" />
          Discarded
        </Badge>
      );
    }
    if (checkout.returned_at) {
      return (
        <Badge variant="success">
          <CheckCircle className="h-3 w-3 mr-1" />
          Returned
        </Badge>
      );
    }

    // Check if overdue
    if (checkout.expected_return_at) {
      const expectedReturn = new Date(checkout.expected_return_at);
      if (expectedReturn < new Date()) {
        return (
          <Badge variant="error">
            <AlertCircle className="h-3 w-3 mr-1" />
            Overdue
          </Badge>
        );
      }
    }

    return (
      <Badge variant="warning">
        <Clock className="h-3 w-3 mr-1" />
        Active
      </Badge>
    );
  };

  const formatDuration = (checkedOutAt: string) => {
    const start = new Date(checkedOutAt);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}d ${diffHours % 24}h`;
    }
    return `${diffHours}h`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Checkouts</h1>
          <p className="text-foreground-muted">Track tool checkouts and returns</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-64">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
            <Input
              placeholder="Search by tool, user, job, or label..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant={statusFilter === 'active' ? 'primary' : 'ghost'}
            onClick={() => setStatusFilter('active')}
          >
            Active
          </Button>
          <Button
            variant={statusFilter === 'returned' ? 'primary' : 'ghost'}
            onClick={() => setStatusFilter('returned')}
          >
            Returned
          </Button>
          <Button
            variant={statusFilter === 'all' ? 'primary' : 'ghost'}
            onClick={() => setStatusFilter('all')}
          >
            All
          </Button>
        </div>
      </div>

      <Card>
        <CardBody>
          {isLoading ? (
            <div className="text-center py-12">
              <div className="spinner h-8 w-8 mx-auto" />
            </div>
          ) : filteredCheckouts?.length === 0 ? (
            <div className="text-center py-12 text-foreground-muted">
              <Package className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No checkouts found</p>
              <p className="text-sm">
                {statusFilter === 'active'
                  ? 'No tools are currently checked out'
                  : 'No checkout records match your search'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Tool</th>
                    <th>User</th>
                    <th>Job</th>
                    <th>Qty</th>
                    <th>Checked Out</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCheckouts?.map((checkout) => (
                    <tr key={checkout.id}>
                      <td>
                        <div>
                          <span className="font-medium">{checkout.tool_name}</span>
                          {checkout.label_code && (
                            <span className="ml-2 text-xs text-foreground-muted font-mono">
                              {checkout.label_code}
                            </span>
                          )}
                          {checkout.part_number && (
                            <p className="text-sm text-foreground-muted">{checkout.part_number}</p>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="flex items-center gap-2">
                          <User className="h-4 w-4 text-foreground-muted" />
                          {checkout.user_name}
                        </span>
                      </td>
                      <td>
                        {checkout.job_number ? (
                          <span className="flex items-center gap-2">
                            <Briefcase className="h-4 w-4 text-foreground-muted" />
                            {checkout.job_number}
                          </span>
                        ) : (
                          <span className="text-foreground-muted">-</span>
                        )}
                      </td>
                      <td>{checkout.quantity}</td>
                      <td className="text-foreground-muted text-sm">
                        {new Date(checkout.checked_out_at).toLocaleString()}
                      </td>
                      <td className="text-foreground-muted text-sm">
                        {checkout.returned_at
                          ? formatDuration(checkout.checked_out_at)
                          : formatDuration(checkout.checked_out_at)}
                      </td>
                      <td>{getStatusBadge(checkout)}</td>
                      <td>
                        {!checkout.returned_at && !checkout.discarded && (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setReturningCheckout(checkout)}
                              title="Return"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDiscardingCheckout(checkout)}
                              title="Discard"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Return Modal */}
      {returningCheckout && (
        <ReturnModal
          checkout={returningCheckout}
          onClose={() => setReturningCheckout(null)}
          onSuccess={() => {
            setReturningCheckout(null);
            queryClient.invalidateQueries({ queryKey: ['checkouts'] });
          }}
        />
      )}

      {/* Discard Modal */}
      {discardingCheckout && (
        <DiscardModal
          checkout={discardingCheckout}
          onClose={() => setDiscardingCheckout(null)}
          onSuccess={() => {
            setDiscardingCheckout(null);
            queryClient.invalidateQueries({ queryKey: ['checkouts'] });
          }}
        />
      )}
    </div>
  );
}

// Return Modal
interface ReturnModalProps {
  checkout: Checkout;
  onClose: () => void;
  onSuccess: () => void;
}

function ReturnModal({ checkout, onClose, onSuccess }: ReturnModalProps) {
  const [formData, setFormData] = useState({
    returnLocationId: '',
    returnCondition: 'good' as 'good' | 'worn' | 'damaged',
    chipTimeHours: '',
    returnNotes: '',
  });
  const [error, setError] = useState('');

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const res = await api.get<Location[]>('/locations');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  const conditionOptions = [
    { value: 'good', label: 'Good - Ready for reuse' },
    { value: 'worn', label: 'Worn - Still usable' },
    { value: 'damaged', label: 'Damaged - Needs repair/disposal' },
  ];

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await api.post(`/checkouts/${checkout.id}/return`, {
        returnLocationId: data.returnLocationId,
        returnCondition: data.returnCondition,
        chipTimeHours: data.chipTimeHours ? parseFloat(data.chipTimeHours) : undefined,
        returnNotes: data.returnNotes || undefined,
      });
      if (!res.success) throw new Error(res.error || 'Failed to return tool');
      return res.data;
    },
    onSuccess: () => {
      setError('');
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to return tool');
    },
  });

  const handleSubmit = () => {
    if (!formData.returnLocationId) {
      setError('Please select a return location');
      return;
    }
    mutation.mutate(formData);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Return Tool"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>
            <RotateCcw className="h-4 w-4" />
            Return Tool
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>}

        <div className="p-4 bg-surface-hover rounded-lg">
          <p className="font-medium">{checkout.tool_name}</p>
          {checkout.label_code && (
            <p className="text-sm text-foreground-muted font-mono">{checkout.label_code}</p>
          )}
          <p className="text-sm text-foreground-muted mt-1">
            Checked out by {checkout.user_name} on{' '}
            {new Date(checkout.checked_out_at).toLocaleString()}
          </p>
        </div>

        <Select
          label="Return Location *"
          options={
            locations?.map((loc) => ({
              value: loc.id,
              label: `${loc.toolroom_name} - ${loc.name}`,
            })) || []
          }
          value={formData.returnLocationId}
          onChange={(e) => setFormData({ ...formData, returnLocationId: e.target.value })}
        />

        <Select
          label="Condition"
          options={conditionOptions}
          value={formData.returnCondition}
          onChange={(e) =>
            setFormData({
              ...formData,
              returnCondition: e.target.value as 'good' | 'worn' | 'damaged',
            })
          }
        />

        <Input
          label="Chip Time (hours)"
          type="number"
          step="0.1"
          min="0"
          value={formData.chipTimeHours}
          onChange={(e) => setFormData({ ...formData, chipTimeHours: e.target.value })}
          placeholder="Time spent cutting"
        />

        <div>
          <label className="block text-sm font-medium mb-1">Notes</label>
          <textarea
            className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm resize-none"
            rows={3}
            value={formData.returnNotes}
            onChange={(e) => setFormData({ ...formData, returnNotes: e.target.value })}
            placeholder="Any notes about the return..."
          />
        </div>
      </div>
    </Modal>
  );
}

// Discard Modal
interface DiscardModalProps {
  checkout: Checkout;
  onClose: () => void;
  onSuccess: () => void;
}

function DiscardModal({ checkout, onClose, onSuccess }: DiscardModalProps) {
  const [formData, setFormData] = useState({
    discardReason: '',
    chipTimeHours: '',
    notes: '',
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await api.post(`/checkouts/${checkout.id}/discard`, {
        discardReason: data.discardReason || undefined,
        chipTimeHours: data.chipTimeHours ? parseFloat(data.chipTimeHours) : undefined,
        notes: data.notes || undefined,
      });
      if (!res.success) throw new Error(res.error || 'Failed to discard tool');
      return res.data;
    },
    onSuccess: () => {
      setError('');
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to discard tool');
    },
  });

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Discard Tool"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => mutation.mutate(formData)} loading={mutation.isPending}>
            <Trash2 className="h-4 w-4" />
            Discard Tool
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>}

        <div className="p-4 bg-error/10 border border-error/30 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-error flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-error">This action cannot be undone</p>
              <p className="text-sm text-foreground-muted mt-1">
                The tool will be marked as discarded and removed from inventory.
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 bg-surface-hover rounded-lg">
          <p className="font-medium">{checkout.tool_name}</p>
          {checkout.label_code && (
            <p className="text-sm text-foreground-muted font-mono">{checkout.label_code}</p>
          )}
        </div>

        <Input
          label="Reason for Discard"
          value={formData.discardReason}
          onChange={(e) => setFormData({ ...formData, discardReason: e.target.value })}
          placeholder="e.g., Broken, Worn out, Consumed"
        />

        <Input
          label="Chip Time (hours)"
          type="number"
          step="0.1"
          min="0"
          value={formData.chipTimeHours}
          onChange={(e) => setFormData({ ...formData, chipTimeHours: e.target.value })}
          placeholder="Time spent before discard"
        />

        <div>
          <label className="block text-sm font-medium mb-1">Additional Notes</label>
          <textarea
            className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm resize-none"
            rows={3}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Any additional details..."
          />
        </div>
      </div>
    </Modal>
  );
}
